<template>
  <div
    class="wrapper_labels col d-flex flex-wrap justify-content-center"
    :class="[type]"
  >
    <div
      class="d-flex"
      :class="{ legend_spaces: legendsWithAll.length > 1 }"
      v-for="legend of legendsWithAll"
      :key="legend.label"
    >
      <Label
        :key="legend.isAll ? legend.label + '_' + allChecked : legend.label + '_'"
        :removeButton="legend.isAll ? false : removeButton"
        :legend="legend"
        :type="type"
        :link="legend.isAll ? false : link"
        :chartName="chartName"
        :isVisible="isDatasetVisible(legend)"
        class="legend"
        @updateDataset="updateDataset"
        @removeDomain="removeDomainHandler"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import getColor from '@/helpers/store/getColor'
import Label from './Label.vue'

export default {
  data() {
    return {
      removeLoading: false,
      allDatasetAdded: false,
      allChecked: false,
      // Метку "All" фиксируем при создании: сравнение с метками датасетов
      // не должно зависеть от текущей локали, а $t недоступен после unmount
      allLabel: this.$t('components.legend.all'),
    }
  },
  props: {
    dataset: Object,
    removeButton: {
      type: Boolean,
      default: true,
    },
    link: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: 'links',
    },
    chartName: String,
  },
  emits: ['updateDataset'],
  components: {
    Label,
  },
  computed: {
    legends() {
      // Все датасеты в виде легенд
      const labels = this.dataset.datasets.map((el, index) => {
        return {
          label: el.label,
          index,
          color: el.borderColor,
          isDataFromGa: el.isDataFromGa,
        }
      })
      return labels
    },
    legendsWithAll() {
      // Получаем только домены (без All датасета)
      const domainLegends = this.legends.filter(l => l.label !== this.allLabel)

      // Если доменов >= 4, добавляем All чекбокс в начало UI
      if (domainLegends.length >= 4) {
        // Проверяем, добавлен ли уже All в датасет
        const allInDataset = this.dataset.datasets.find(d => d.label === this.allLabel)

        return [
          {
            label: this.allLabel,
            // Если All уже добавлен в датасет, используем его реальный индекс (0)
            // Иначе используем -1
            index: allInDataset ? 0 : -1,
            color: '#8B5CF6',
            isAll: true,
          },
          ...domainLegends
        ]
      }

      // Если доменов меньше 4, показываем только домены
      return domainLegends
    },
  },
  watch: {
    // Следим за изменениями количества доменов
    'dataset.datasets': {
      handler(newVal, oldVal) {
        if (!oldVal) {
          // Первая загрузка - проверяем есть ли уже All
          const allDataset = newVal.find(d => d.label === this.allLabel)
          if (allDataset) {
            this.allDatasetAdded = true
            this.allChecked = !allDataset.hidden
          }
          return
        }

        const newDomainCount = newVal.filter(d => d.label !== this.allLabel).length
        const oldDomainCount = oldVal.filter(d => d.label !== this.allLabel).length

        // Если количество доменов изменилось
        if (newDomainCount !== oldDomainCount) {
          // Удаляем All dataset если он был добавлен
          if (this.allDatasetAdded) {
            const allIndex = this.dataset.datasets.findIndex(d => d.label === this.allLabel)
            if (allIndex !== -1) {
              this.dataset.datasets.splice(allIndex, 1)
            }
            this.allDatasetAdded = false
            this.allChecked = false
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['removeDomain']),
    getColor,
    isDatasetVisible(legend) {
      // Для All чекбокса используем специальную логику
      if (legend.label === this.allLabel) {
        // Если датасет All существует, проверяем его реальное состояние
        const allDataset = this.dataset.datasets.find(d => d.label === this.allLabel)
        if (allDataset) {
          // Синхронизируем allChecked с реальным состоянием датасета
          const visible = !allDataset.hidden
          if (this.allChecked !== visible) {
            this.allChecked = visible
          }
          return visible
        }
        // Если датасета нет, возвращаем false
        return false
      }

      // Для остальных датасетов проверяем их состояние hidden
      const dataset = this.dataset.datasets[legend.index]
      return dataset ? !dataset.hidden : true
    },
    updateDataset(index) {
      console.log('LegendDomains.updateDataset, index:', index, 'allDatasetAdded:', this.allDatasetAdded)

      // Если index -1 или (index 0 и первый датасет это All), значит кликнули на All
      const isAllClick = index === -1 || (index === 0 && this.dataset.datasets[0]?.label === this.allLabel)

      if (isAllClick) {
        this.toggleAllDataset()
        return
      }

      // Обычный домен - передаем дальше
      this.$emit('updateDataset', index)
    },
    async removeDomainHandler(domain) {
      if (this.removeLoading) return
      this.removeLoading = true
      await this.removeDomain({ domains: [domain] })
      this.removeLoading = false
    },
    toggleAllDataset() {
      console.log('toggleAllDataset called, allDatasetAdded:', this.allDatasetAdded, 'allChecked:', this.allChecked)

      if (!this.allDatasetAdded) {
        // Первый раз - добавляем All dataset
        // Добавляем датасет
        this.addAllDataset()

        // После добавления датасета устанавливаем состояние
        this.allChecked = true
        console.log('First time: allChecked set to true AFTER adding dataset')

        // Форсируем обновление компонентов
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      } else {
        // All уже добавлен - переключаем состояние
        const allIndex = this.dataset.datasets.findIndex(d => d.label === this.allLabel)
        console.log('Toggle existing All at index:', allIndex)

        if (allIndex !== -1) {
          // Сначала меняем состояние чекбокса
          this.allChecked = !this.allChecked
          console.log('allChecked toggled to:', this.allChecked)

          // Затем обновляем датасет
          this.dataset.datasets[allIndex].hidden = !this.allChecked
          console.log('dataset hidden set to:', this.dataset.datasets[allIndex].hidden)

          // Форсируем обновление компонентов
          this.$nextTick(() => {
            this.$forceUpdate()
          })

          // Обновляем график
          this.$emit('updateDataset', allIndex)
        }
      }
    },
    addAllDataset() {
      // Получаем все домены (без All)
      const datasets = this.dataset.datasets.filter(d => d.label !== this.allLabel)

      if (datasets.length === 0) return

      // Вычисляем сумму для каждой точки времени
      const dataLength = datasets[0].data.length
      const sumData = []

      for (let i = 0; i < dataLength; i++) {
        let sum = 0
        datasets.forEach(dataset => {
          if (dataset.data[i] !== null && dataset.data[i] !== undefined) {
            sum += dataset.data[i]
          }
        })
        sumData.push(sum)
      }

      // Создаем новый dataset для All со всеми необходимыми свойствами
      const allDataset = {
        label: this.allLabel,
        data: sumData,
        borderColor: '#8B5CF6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        hidden: false, // Показываем при добавлении
        isPredicted: false,
        pointHoverBackgroundColor: '#8B5CF6',
        pointHoverBorderColor: '#8B5CF6',
        fill: false,
        tension: 0.3,
        borderWidth: 3,
        // Важно: добавляем порядковый индекс для сортировки в tooltip
        order: -1, // Отрицательное значение, чтобы был первым
      }

      // Добавляем All в начало массива датасетов (чтобы был первым в тултипе)
      this.dataset.datasets.unshift(allDataset)
      this.allDatasetAdded = true

      console.log('All dataset added. Datasets order:', this.dataset.datasets.map(d => d.label))

      // Форсируем обновление родительского компонента чтобы пересчитался periodFilter
      this.$nextTick(() => {
        this.$parent.$parent.$forceUpdate()
      })
    },
  },
}
</script>

<style scoped>
.legend_spaces {
  margin: 8px 34px 8px 0;
}
.legend_spaces:last-child {
  margin-right: 0;
}

@media (max-width: 768px) {
  .legend_spaces {
    margin: 6px 8px 6px 0;
  }
}

.checkbox_button .legend_spaces {
  margin-right: 10px;
}
</style>
